import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FaEnvelope, FaShoppingCart, FaCommentAlt, FaHeart, FaUserPlus, FaAt, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AppDispatch } from '../../redux/Store';
import { updateEmailNotificationPreferences, fetchEmailNotificationPreferences, EmailNotificationPreferences } from '../../redux/thunks/userPreferencesThunks';
import {
  SettingsPanel,
  SettingsRow,
  SettingsToggle,
  SettingsSaveButton,
  SettingsNote,
  SettingsSaveStatus,
} from './primitives';

export const EmailNotificationSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [preferences, setPreferences] = useState<EmailNotificationPreferences>({
    profilePurchase: true,
    profileSale: true,
    newMessage: true,
    newComment: true,
    newLike: true,
    newFollow: true,
    mentionInPost: true,
    newPostFromSubscription: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const fetchedPreferences = await dispatch(fetchEmailNotificationPreferences()).unwrap();
      if (fetchedPreferences) {
        setPreferences({
          profilePurchase: fetchedPreferences.profilePurchase ?? true,
          profileSale: fetchedPreferences.profileSale ?? true,
          newMessage: fetchedPreferences.newMessage ?? true,
          newComment: fetchedPreferences.newComment ?? true,
          newLike: fetchedPreferences.newLike ?? true,
          newFollow: fetchedPreferences.newFollow ?? true,
          mentionInPost: fetchedPreferences.mentionInPost ?? true,
          newPostFromSubscription: fetchedPreferences.newPostFromSubscription ?? true,
        });
      }
    } catch (error) {
      console.log('Using default email notification preferences');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSaveStatus('saving');

    try {
      await dispatch(updateEmailNotificationPreferences(preferences)).unwrap();
      setSaveStatus('saved');
      toast.success('Email notification preferences saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save email notification preferences:', error);
      setSaveStatus('error');
      toast.error('Failed to save email notification preferences');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (key: keyof EmailNotificationPreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const notificationTypes: Array<{
    key: keyof EmailNotificationPreferences;
    icon: React.ReactNode;
    label: string;
    description: string;
  }> = [
    { key: 'profilePurchase', icon: <FaShoppingCart />, label: 'Profile Purchases', description: 'Get notified when someone purchases your profile' },
    { key: 'profileSale',     icon: <FaShoppingCart />, label: 'Profile Sales',     description: 'Get notified when you successfully purchase a profile' },
    { key: 'newMessage',      icon: <FaEnvelope />,     label: 'New Messages',      description: 'Get notified when you receive a new direct message' },
    { key: 'newComment',      icon: <FaCommentAlt />,   label: 'New Comments',      description: 'Get notified when someone comments on your post' },
    { key: 'newLike',         icon: <FaHeart />,        label: 'Likes & Reactions', description: 'Get notified when someone likes or reacts to your post' },
    { key: 'newFollow',       icon: <FaUserPlus />,     label: 'New Followers',     description: 'Get notified when someone follows you' },
    { key: 'mentionInPost',   icon: <FaAt />,           label: 'Mentions',          description: 'Get notified when someone mentions you in a post' },
    { key: 'newPostFromSubscription', icon: <FaFileAlt />, label: 'New Posts from Subscriptions', description: 'Get notified when a creator you subscribe to publishes a new paid or PPV post' },
  ];

  return (
    <SettingsPanel
      title="Email Notifications"
      description="Choose which notifications you want to receive via email"
    >
      {notificationTypes.map((type) => (
        <SettingsRow
          key={type.key}
          icon={type.icon}
          title={type.label}
          description={type.description}
          control={
            <SettingsToggle
              checked={preferences[type.key] ?? false}
              onChange={() => handleToggle(type.key)}
              ariaLabel={`Toggle ${type.label}`}
            />
          }
        />
      ))}

      <SettingsSaveButton
        status={saveStatus}
        onClick={handleSave}
        disabled={isLoading}
        idleLabel="Save Preferences"
      />

      <SettingsNote tone="info">
        <p>
          <strong>Note:</strong> Make sure your email address is verified to receive notifications.
          You can manage your email address in your account settings.
        </p>
      </SettingsNote>
    </SettingsPanel>
  );
};

export default EmailNotificationSettings;
